import React, { useState, useEffect } from 'react';
import { getUsers, createUser, updateUser, deleteUser } from '../utils/dataManager';
import { canManageUsers, getCurrentUser } from '../utils/auth';
import { validateUserForm } from '../utils/validation';
import { FiEdit, FiTrash2, FiKey, FiSearch } from 'react-icons/fi';
import './UserManagement.css';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const currentUser = getCurrentUser();
  const hasPermission = canManageUsers();

  // Helper function to check if current user can manage target user
  const canManageUser = (targetUser) => {
    if (!currentUser || !targetUser) return false;
    if (currentUser.role === 'super_admin') return true;
    if (currentUser.role === 'admin' && targetUser.role === 'super_admin') return false;
    if (currentUser.role === 'admin') return true;
    
    return false;
  };

  useEffect(() => {
    if (hasPermission) {
      loadUsers();
    }
  }, [hasPermission]);
  if (!hasPermission) {
    return (
      <div className="no-permission">
        <h2>Access Denied</h2>
        <p>You don't have permission to manage users.</p>
      </div>
    );
  }

  const loadUsers = async () => {
    setLoading(true);
    try {
      const allUsers = await getUsers();
      setUsers(allUsers || []); 
      setFilteredUsers(allUsers || []);
    } catch (error) {
      console.error('Error loading users:', error);
      setUsers([]);
      setFilteredUsers([]);
    }
    setLoading(false);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setFilteredUsers(users);
      return;
    }

    const filtered = users.filter(user => 
      user.username.toLowerCase().includes(query.toLowerCase()) ||
      user.full_name?.toLowerCase().includes(query.toLowerCase()) ||
      user.role.toLowerCase().includes(query.toLowerCase()) ||
      (currentUser?.role === 'super_admin' || user.role !== 'super_admin' ? 
        user.email.toLowerCase().includes(query.toLowerCase()) : false)
    );
    
    setFilteredUsers(filtered);
  };

  const handleAddUser = () => {
    setEditingUser(null);
    setShowForm(true);
  };

  const handleEditUser = (user) => {
    // Admin cannot edit superadmin
    if (currentUser?.role === 'admin' && user?.role === 'super_admin') {
      alert("Admin cannot edit Super Admin accounts!");
      return;
    }
    
    setEditingUser(user);
    setShowForm(true);
  };

  const handleDeleteUser = async (userId) => {
    if (userId === currentUser?.id) {
      alert("You cannot delete your own account!");
      return;
    }
    const targetUser = users.find(user => user.id === userId);
    if (currentUser?.role === 'admin' && targetUser?.role === 'super_admin') {
      alert("Admin cannot delete Super Admin accounts!");
      return;
    }

    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await deleteUser(userId);
        loadUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
        alert('Failed to delete user');
      }
    }
  };

  const handleResetPassword = async (userId) => {
    const targetUser = users.find(user => user.id === userId);
    if (currentUser?.role === 'admin' && targetUser?.role === 'super_admin') {
      alert("Admin cannot reset Super Admin passwords!");
      return;
    }

    const newPassword = prompt('Enter new password for user:');
    if (newPassword && newPassword.length >= 6) {
      try {
        await updateUser(userId, { password: newPassword });
        alert('Password has been reset successfully!');
        loadUsers();
      } catch (error) {
        console.error('Error resetting password:', error);
        alert('Failed to reset password');
      }
    } else if (newPassword) {
      alert('Password must be at least 6 characters long!');
    }
  };

  const handleFormSubmit = async (userData) => {
    try {
      console.log('UserManagement - Submitting user data:', userData);
      
      // VALIDATE: Ensure role is only one of the 3 allowed values
      const allowedRoles = ['editor', 'admin', 'super_admin'];
      if (!allowedRoles.includes(userData.role)) {
        console.error('Invalid role:', userData.role, '- forcing to editor');
        userData.role = 'editor'; 
      }
      
      if (editingUser) {
        console.log('UserManagement - Updating user ID:', editingUser.id);
        await updateUser(editingUser.id, userData);
      } else {
        console.log('UserManagement - Creating new user');
        await createUser(userData);
      }
      setShowForm(false);
      setEditingUser(null);
      loadUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      
      // Better error messages
      let errorMessage = 'Failed to save user';
      if (error.message) {
        if (error.message.includes('Duplicate entry') && error.message.includes('email')) {
          errorMessage = '❌ Email already exists! Please use a different email address.';
        } else if (error.message.includes('Duplicate entry') && error.message.includes('username')) {
          errorMessage = '❌ Username already exists! Please choose a different username.';
        } else if (error.message.includes('role')) {
          errorMessage = '❌ Invalid role selected. Please refresh and try again.';
        } else {
          errorMessage = error.message;
        }
      }
      
      alert(errorMessage);
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingUser(null);
  };

  if (loading) {
    return <div className="loading">Loading users...</div>;
  }

  return (
    <div className="user-management">
      <div className="page-header">
        <h1>User Management</h1>
        <div className="header-actions">
          <div className="search-container">
            <FiSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="search-input"
            />
          </div>
          <button onClick={handleAddUser} className="btn btn-primary">
            Add New User
          </button>
        </div>
      </div>

      {showForm && (
        <UserForm
          user={editingUser}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
        />
      )}

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Email</th>
              <th>Role</th>
              <th>Created At</th>
              <th>Last Login</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {(filteredUsers || []).map(user => (
              <tr key={user.id}>
                <td>{user.username}</td>
                <td>
                  {currentUser?.role === 'super_admin' || user.role !== 'super_admin' 
                    ? user.email 
                    : '••••••••@••••••••'}
                </td>
                <td>
                  <span className={`role-badge ${user.role}`}>
                    {user.role.replace('_', ' ').toUpperCase()}
                  </span>
                </td>
                <td>{new Date(user.created_at).toLocaleDateString()}</td>
                <td>
                  {user.last_login 
                    ? new Date(user.last_login).toLocaleDateString()
                    : 'Never'
                  }
                </td>
                <td>
                  <div className="action-buttons">
                    {canManageUser(user) && (
                      <>
                        <button 
                          onClick={() => handleEditUser(user)}
                          className="btn btn-primary btn-sm btn-icon"
                          title="Edit User"
                        >
                          <FiEdit />
                        </button>
                        <button 
                          onClick={() => handleResetPassword(user.id)}
                          className="btn btn-secondary btn-sm btn-icon"
                          title="Reset Password"
                        >
                          <FiKey />
                        </button>
                      </>
                    )}
                    {canManageUser(user) && user.id !== currentUser.id && (
                      <button 
                        onClick={() => handleDeleteUser(user.id)}
                        className="btn btn-danger btn-sm btn-icon"
                        title="Delete User"
                      >
                        <FiTrash2 />
                      </button>
                    )}
                    {!canManageUser(user) && user.role === 'super_admin' && (
                      <span className="no-permission-text">No Access</span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="users-cards">
        {(filteredUsers || []).map(user => (
          <div key={user.id} className="user-card">
            <div className="user-card-header">
              <div className="user-card-info">
                <h4>{user.username}</h4>
                <p className="user-email">
                  {currentUser?.role === 'super_admin' || user.role !== 'super_admin' 
                    ? user.email 
                    : '••••••••@••••••••'}
                </p>
                <span className={`role-badge ${user.role}`}>
                  {user.role.replace('_', ' ').toUpperCase()}
                </span>
              </div>
            </div>
            
            <div className="user-card-details">
              <div className="user-card-detail">
                <label>Created At</label>
                <span>{new Date(user.created_at).toLocaleDateString()}</span>
              </div>
              <div className="user-card-detail">
                <label>Last Login</label>
                <span>
                  {user.last_login 
                    ? new Date(user.last_login).toLocaleDateString()
                    : 'Never'
                  }
                </span>
              </div>
            </div>
            
            <div className="user-card-actions">
              {canManageUser(user) && (
                <>
                  <button 
                    onClick={() => handleEditUser(user)}
                    className="btn btn-primary btn-sm btn-icon"
                    title="Edit User"
                  >
                    <FiEdit />
                  </button>
                  <button 
                    onClick={() => handleResetPassword(user.id)}
                    className="btn btn-secondary btn-sm btn-icon"
                    title="Reset Password"
                  >
                    <FiKey />
                  </button>
                </>
              )}
              {canManageUser(user) && user.id !== currentUser.id && (
                <button 
                  onClick={() => handleDeleteUser(user.id)}
                  className="btn btn-danger btn-sm btn-icon"
                  title="Delete User"
                >
                  <FiTrash2 />
                </button>
              )}
              {!canManageUser(user) && user.role === 'super_admin' && (
                <div className="no-permission">
                  <span className="no-permission-text">No Access to Super Admin</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const UserForm = ({ user, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    full_name: '',
    role: 'editor'  // Default to editor role
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const currentUser = getCurrentUser();

  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username,
        email: user.email,
        password: '', 
        full_name: user.full_name || '',
        role: user.role
      });
    }
  }, [user]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: ''
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Only regular admin cannot assign super_admin role (super_admin can)
    if (currentUser?.role === 'admin' && formData.role === 'super_admin') {
      setErrors({ role: 'Only Super Admin can assign Super Admin role' });
      setLoading(false);
      return;
    }
    
    const validationErrors = validateUserForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setLoading(false);
      return;
    }
    
    try {
      const submitData = { ...formData };
      if (user && !formData.password) {
        delete submitData.password;
      }
      
      await onSubmit(submitData);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="user-form-overlay">
      <div className="user-form">
        <div className="form-header">
          <h2>{user ? 'Edit User' : 'Add New User'}</h2>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="username">Username *</label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              className={errors.username ? 'error' : ''}
              required
            />
            {errors.username && <span className="error-text">{errors.username}</span>}
          </div>
          
          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={errors.email ? 'error' : ''}
              required
            />
            {errors.email && <span className="error-text">{errors.email}</span>}
          </div>
          
          <div className="form-group">
            <label htmlFor="password">
              Password * {user && <small>(leave empty to keep current password)</small>}
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className={errors.password ? 'error' : ''}
              required={!user}
            />
            {errors.password && <span className="error-text">{errors.password}</span>}
          </div>
          
          <div className="form-group">
            <label htmlFor="full_name">Full Name *</label>
            <input
              type="text"
              id="full_name"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              className={errors.full_name ? 'error' : ''}
              required
            />
            {errors.full_name && <span className="error-text">{errors.full_name}</span>}
          </div>
          
          <div className="form-group">
            <label htmlFor="role">Role *</label>
            <select
              id="role"
              name="role"
              value={formData.role}
              onChange={handleChange}
              className={errors.role ? 'error' : ''}
              required
            >
              <option value="editor">Editor</option>
              <option value="admin">Admin</option>
              {currentUser?.role === 'super_admin' && (
                <option value="super_admin">Super Admin</option>
              )}
            </select>
            {errors.role && <span className="error-text">{errors.role}</span>}
            {currentUser?.role === 'admin' && (
              <small className="role-restriction-note">
                Admin users cannot create or assign Super Admin role
              </small>
            )}
          </div>
          
          <div className="form-actions">
            <button type="button" onClick={onCancel} className="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="btn btn-primary">
              {loading ? 'Saving...' : (user ? 'Update User' : 'Create User')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserManagement;
